import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ContentPageTheme,
  Form,
  Player,
  PlayersServices,
  PublicReviewWidget,
  ReviewWidgetConfig,
  ReviewWidgetConfigServices,
} from "@/services/bedrock";
import { publicWebOrigin, usePaginatedCombobox } from "@/utils";
import { buildReviewWidgetPreviewData } from "./reviewWidgetPreviewData";

const PLAYER_ID_PLACEHOLDER = "{{player_id}}";

const EMBED_IFRAME_HEIGHT = 480;
const MOBILE_PREVIEW_WIDTH = 390;

type PreviewDevice = "web" | "mobile";

interface UseReviewWidgetPreviewArgs {
  config: ReviewWidgetConfig;
  themes: ContentPageTheme[];
  forms: Form[];
}

const useReviewWidgetPreview = ({ config, themes, forms }: UseReviewWidgetPreviewArgs) => {
  const [includeAutoResize, setIncludeAutoResize] = useState(true);
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>("web");
  const [previewPlayer, setPreviewPlayer] = useState<Player | null>(null);

  const getPlayers = useCallback(async (search: string | undefined, page: number) => {
    return PlayersServices.getPlayers({
      search,
      page,
      perPage: 25,
      sort: "first_name",
      sortDirection: "asc",
    });
  }, []);

  const players = usePaginatedCombobox(getPlayers, false, previewPlayer);
  useEffect(() => {
    setPreviewPlayer(players.value ?? null);
  }, [players.value]);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isIFrameConnected, setIsIFrameConnected] = useState(0);
  const [iframeHeight, setIframeHeight] = useState(EMBED_IFRAME_HEIGHT);
  const iframeOrigin = publicWebOrigin();
  const isSamplePreview = !previewPlayer?.guid;
  const iframeSrc = `${iframeOrigin}/embed/review/preview`;
  const previewPlayerName = previewPlayer?.fullName || undefined;

  const previewOverrides = useMemo(
    () => ({
      showStarSummary: config.showStarSummary,
      showSentimentSummary: config.showSentimentSummary,
      showNpsSummary: config.showNpsSummary,
      formId: config.formId ?? null,
      contentPageThemeId: config.contentPageThemeId ?? null,
      reviewSortType: config.reviewSortType,
      useCondensedQuotes: config.useCondensedQuotes,
      approvedForSocialOnly: config.approvedForSocialOnly,
      autoScrollEnabled: config.autoScrollEnabled,
    }),
    [config]
  );

  const {
    data: playerPreviewData,
    isFetching: isFetchingPlayerPreview,
    isError: isPreviewError,
    error: previewError,
  } = useQuery({
    queryKey: [
      "reviewWidgetPublicPreview",
      config.slug,
      previewPlayer?.guid,
      previewOverrides.formId,
      previewOverrides.contentPageThemeId,
      previewOverrides.reviewSortType,
      previewOverrides.approvedForSocialOnly,
      previewOverrides.showStarSummary,
      previewOverrides.showSentimentSummary,
      previewOverrides.showNpsSummary,
    ],
    queryFn: () => ReviewWidgetConfigServices.getPublicPreview(config.slug, previewPlayer!.guid, previewOverrides),
    enabled: !!config.slug && !!previewPlayer?.guid,
    placeholderData: previousData => previousData,
  });

  useEffect(() => {
    if (!isPreviewError) return;
    console.error(previewError);
    toast.error("Error loading player preview");
  }, [isPreviewError, previewError]);

  const previewData = useMemo(() => {
    if (previewPlayer?.guid) {
      if (isPreviewError || !playerPreviewData) return null;

      const { formId, contentPageThemeId, showStarSummary, showSentimentSummary, showNpsSummary, ...configOverrides } =
        previewOverrides;
      const selectedTheme = contentPageThemeId ? themes.find(theme => theme.id === contentPageThemeId) : null;
      const selectedForm = formId ? forms.find(form => form.id === formId) : null;
      const summary = {
        ...(showStarSummary ? { star: playerPreviewData.summary?.star } : {}),
        ...(showSentimentSummary ? { sentiment: playerPreviewData.summary?.sentiment } : {}),
        ...(showNpsSummary ? { nps: playerPreviewData.summary?.nps } : {}),
      };

      return {
        ...playerPreviewData,
        ...configOverrides,
        summary: Object.keys(summary).length > 0 ? summary : null,
        ...(selectedTheme ? { theme: selectedTheme } : {}),
        ...(selectedForm
          ? { formUrl: playerPreviewData.formUrl ?? selectedForm.testRequest?.url ?? "#write-a-review" }
          : {}),
      };
    }
    return buildReviewWidgetPreviewData(config, themes);
  }, [config, themes, forms, previewPlayer?.guid, playerPreviewData, previewOverrides, isPreviewError]);

  const [previewLoadedForGuid, setPreviewLoadedForGuid] = useState<string | null>(null);
  useEffect(() => {
    if (!previewPlayer?.guid) {
      setPreviewLoadedForGuid(null);
      return;
    }
    if (!isFetchingPlayerPreview) {
      setPreviewLoadedForGuid(previewPlayer.guid);
    }
  }, [previewPlayer?.guid, isFetchingPlayerPreview]);

  const isLoading = !!previewPlayer?.guid && previewPlayer.guid !== previewLoadedForGuid;

  const embedUrl = useMemo(() => {
    if (!config.slug) return "";
    const player = previewPlayer?.guid ? encodeURIComponent(previewPlayer.guid) : PLAYER_ID_PLACEHOLDER;
    return `${iframeOrigin}/embed/review/${config.slug}?player=${player}`;
  }, [config.slug, iframeOrigin, previewPlayer?.guid]);

  const iframeSnippet = useMemo(() => {
    if (!embedUrl) return "";

    if (!includeAutoResize) {
      return `<iframe src="${embedUrl}" width="100%" height="${EMBED_IFRAME_HEIGHT}"></iframe>`;
    }

    return `<iframe id="sc-reputation-embed" src="${embedUrl}" width="100%" height="${EMBED_IFRAME_HEIGHT}"></iframe>
<!-- Auto-resizes the iframe to match content height -->
<script>
  window.addEventListener("message", function (e) {
    if (e.data && e.data.type === "REVIEW_WIDGET_HEIGHT" && typeof e.data.height === "number") {
      var f = document.getElementById("sc-reputation-embed");
      if (f) f.style.height = e.data.height + "px";
    }
  });
</script>`;
  }, [embedUrl, includeAutoResize]);

  const sendPayloadToIFrame = useCallback(
    (payload: PublicReviewWidget | null | undefined) => {
      if (!isIFrameConnected || !iframeRef.current?.contentWindow) return;
      iframeRef.current.contentWindow.postMessage({ type: "REVIEW_WIDGET", payload }, iframeOrigin);
    },
    [isIFrameConnected, iframeOrigin]
  );

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (event.origin !== iframeOrigin) return;
      if (event.data?.type === "REVIEW_WIDGET_HEIGHT" && typeof event.data.height === "number") {
        setIframeHeight(event.data.height);
        return;
      }
      setIsIFrameConnected(prev => prev + 1);
    },
    [iframeOrigin]
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleMessage]);

  useEffect(() => {
    setIframeHeight(EMBED_IFRAME_HEIGHT);
    setIsIFrameConnected(0);
  }, [iframeSrc]);

  useEffect(() => {
    if (isIFrameConnected) {
      sendPayloadToIFrame(previewData);
    }
  }, [isIFrameConnected, previewData, sendPayloadToIFrame]);

  return {
    controlsProps: {
      players,
      includeAutoResize,
      onIncludeAutoResizeChange: setIncludeAutoResize,
      previewDevice,
      onPreviewDeviceChange: setPreviewDevice,
      embedUrl,
      isLoading,
      previewPlayerName,
      isSamplePreview,
    },
    previewProps: {
      iframeRef,
      iframeSrc,
      includeAutoResize,
      iframeHeight,
      previewDevice,
      isLoading,
      previewPlayerName,
      iframeSnippet,
    },
  };
};

export { useReviewWidgetPreview, EMBED_IFRAME_HEIGHT, MOBILE_PREVIEW_WIDTH };
export type { PreviewDevice };
